import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, School, Users, GraduationCap, CreditCard, Calendar } from 'lucide-react'
import supabase from '../../services/supabaseClient'

export default function SchoolDetails() {
  const { id } = useParams()
  const [school, setSchool] = useState(null)
  const [stats, setStats] = useState({
    teachers: 0,
    students: 0,
    parents: 0
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchSchoolDetails()
  }, [id])

  async function fetchSchoolDetails() {
    try {
      // Get school details
      const { data: schoolData, error: schoolError } = await supabase
        .from('schools')
        .select('*')
        .eq('id', id)
        .single()

      if (schoolError) throw schoolError
      setSchool(schoolData)

      // Get stats
      const { count: teachersCount } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .eq('school_id', id)
        .eq('role', 'teacher')

      const { count: studentsCount } = await supabase
        .from('learners')
        .select('*', { count: 'exact', head: true })
        .eq('school_id', id)

      const { count: parentsCount } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .eq('school_id', id)
        .eq('role', 'parent')

      setStats({
        teachers: teachersCount || 0,
        students: studentsCount || 0,
        parents: parentsCount || 0
      })
    } catch (error) {
      console.error('Error fetching school details:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  if (!school) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">School not found</p>
        <Link to="/admin/super/schools" className="text-primary-600 hover:text-primary-500 mt-2 inline-block">
          Back to Schools
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link
          to="/admin/super/schools"
          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">School Details</h1>
      </div>

      {/* School Info Card */}
      <div className="card p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center">
            <div className="h-16 w-16 rounded-lg bg-primary-100 flex items-center justify-center">
              <School className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-4">
              <h2 className="text-xl font-bold text-gray-900">{school.name}</h2>
              <p className="text-gray-500">{school.address}, {school.city}</p>
              <div className="flex items-center mt-2 space-x-4">
                <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                  {school.code}
                </code>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  school.subscription_status === 'trial' ? 'bg-warning-100 text-warning-800' :
                  school.subscription_status === 'active' ? 'bg-success-100 text-success-800' :
                  'bg-danger-100 text-danger-800'
                }`}>
                  {school.subscription_status}
                </span>
              </div>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Plan</p>
            <p className="text-lg font-semibold capitalize">{school.subscription_plan}</p>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-primary-100">
              <Users className="h-6 w-6 text-primary-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Teachers</p>
              <p className="text-2xl font-bold text-gray-900">{stats.teachers}</p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-success-100">
              <GraduationCap className="h-6 w-6 text-success-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Students</p>
              <p className="text-2xl font-bold text-gray-900">{stats.students}</p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-warning-100">
              <Users className="h-6 w-6 text-warning-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Parents</p>
              <p className="text-2xl font-bold text-gray-900">{stats.parents}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-gray-500">School Email</p>
              <p className="text-gray-900">{school.email || 'Not provided'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Phone</p>
              <p className="text-gray-900">{school.phone || 'Not provided'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Website</p>
              <p className="text-gray-900">{school.website || 'Not provided'}</p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Principal Information</h3>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-gray-500">Name</p>
              <p className="text-gray-900">{school.principal_name || 'Not provided'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Email</p>
              <p className="text-gray-900">{school.principal_email || 'Not provided'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Phone</p>
              <p className="text-gray-900">{school.principal_phone || 'Not provided'}</p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Admin Information</h3>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-gray-500">Name</p>
              <p className="text-gray-900">{school.admin_name || 'Not provided'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Email</p>
              <p className="text-gray-900">{school.admin_email}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Phone</p>
              <p className="text-gray-900">{school.admin_phone || 'Not provided'}</p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Subscription Details</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <p className="text-sm text-gray-500">Status</p>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                school.subscription_status === 'trial' ? 'bg-warning-100 text-warning-800' :
                school.subscription_status === 'active' ? 'bg-success-100 text-success-800' :
                'bg-danger-100 text-danger-800'
              }`}>
                {school.subscription_status}
              </span>
            </div>
            {school.trial_ends_at && (
              <div className="flex justify-between">
                <p className="text-sm text-gray-500">Trial Ends</p>
                <p className="text-gray-900">{new Date(school.trial_ends_at).toLocaleDateString()}</p>
              </div>
            )}
            <div className="flex justify-between">
              <p className="text-sm text-gray-500">Created</p>
              <p className="text-gray-900">{new Date(school.created_at).toLocaleDateString()}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}